import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Card,
  Statistic,
  Select,
  DatePicker,
  Button,
  Table,
  Space,
  Tag,
  message,
  Typography,
} from "antd";
import { Dayjs } from "dayjs";
import RobinPage from "../components/core/RobinPage";
import { useGetAllMyStoresQuery, useGetOrderStatisticsLazyQuery } from "../graphql/generated/hooks";
import { ORDER_STATUSES, getOrderStatusColor } from "../common/orderStatus";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Using generated hooks for order statistics

function Dashboard() {
  const { t } = useTranslation();

  // Stores for filter
  const { data: storesData, loading: storesLoading } = useGetAllMyStoresQuery();

  // Filters state
  const [storeId, setStoreId] = useState<number | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const [fetchStatistics, { loading, data }] = useGetOrderStatisticsLazyQuery({
    fetchPolicy: "network-only",
    onError: (err) => {
      // eslint-disable-next-line
      message.error(err.message || "Failed to fetch statistics");
    },
  });

  // Fetch on initial mount with empty filters
  useEffect(() => {
    fetchStatistics({
      variables: {
        input: {
          startDate: null,
          endDate: null,
          status: null,
          storeId: null,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = useCallback(() => {
    const startDate = dateRange?.[0]?.startOf("day").toISOString() ?? null;
    const endDate = dateRange?.[1]?.endOf("day").toISOString() ?? null;
    fetchStatistics({
      variables: {
        input: {
          startDate,
          endDate,
          status: status || null,
          storeId: storeId ?? null,
        },
      },
    });
  }, [dateRange, fetchStatistics, status, storeId]);

  const onReset = useCallback(() => {
    setStoreId(null);
    setStatus(null);
    setDateRange(null);
  }, []);

  const storesOptions = useMemo(
    () =>
      storesData?.getAllStoresForPartner?.map((s) => ({
        label: `${s.name} (ID: ${s.id})`,
        value: s.id,
      })) ?? [],
    [storesData]
  );

  const statusOptions = useMemo(
    () => ORDER_STATUSES.map((s) => ({ label: t(`status.${s.toLowerCase()}`), value: s })),
    [t]
  );

  const statistics = data?.getOrderStatistics;

  // Interactive SVG donut for status breakdown
  type StatusSlice = { status: string; percentage: number; count: number; color: string };

  const getStatusHexColor = useCallback((s: string): string => {
    const key = (s || "").toUpperCase();
    const map: Record<string, string> = {
      CREATED: "#faad14",
      WAITING_SCHEDULED: "#faad14",
      WAITING_RESTAURANT: "#2f54eb",
      ACCEPTED: "#52c41a",
      REJECTED: "#ff4d4f",
      PREPARING: "#fa8c16",
      READY_FOR_PICKUP: "#1677ff",
      PICKED_UP: "#13c2c2",
      ARRIVED_AT_CUSTOMER: "#722ed1",
      CANCELLED: "#fa541c",
    };
    return map[key] || "#d9d9d9";
  }, []);

  const slices: StatusSlice[] = useMemo(() => {
    const breakdown = statistics?.statusBreakdown || [];
    return breakdown.map((b: any) => ({
      status: String(b.status),
      percentage: Number(b.percentage || 0),
      count: Number(b.count || 0),
      color: getStatusHexColor(String(b.status)),
    }));
  }, [getStatusHexColor, statistics?.statusBreakdown]);

  const donutRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; content: string }>({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });

  function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
    const rad = ((angle - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(cx: number, cy: number, rOuter: number, rInner: number, start: number, end: number) {
    const startOuter = polarToCartesian(cx, cy, rOuter, end);
    const endOuter = polarToCartesian(cx, cy, rOuter, start);
    const startInner = polarToCartesian(cx, cy, rInner, start);
    const endInner = polarToCartesian(cx, cy, rInner, end);
    const largeArc = end - start <= 180 ? 0 : 1;
    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
      `L ${startInner.x} ${startInner.y}`,
      `A ${rInner} ${rInner} 0 ${largeArc} 1 ${endInner.x} ${endInner.y}`,
      "Z",
    ].join(" ");
  }

  return (
    <RobinPage title={t("label.menu.dashboard")}>
      <Card style={{ marginBottom: 16 }} title={t("label.filters")}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Text strong>{t("store")}</Text>
              <Select
                style={{ width: "100%" }}
                placeholder={t("placeholder.select_stores")}
                loading={storesLoading}
                options={storesOptions}
                allowClear
                value={storeId ?? undefined}
                onChange={(v) => setStoreId(typeof v === "number" ? v : null)}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Text strong>{t("label.status")}</Text>
              <Select
                style={{ width: "100%" }}
                placeholder={t("label.all")}
                options={statusOptions}
                allowClear
                value={status ?? undefined}
                onChange={(v) => setStatus((v as string) || null)}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              <Text strong>{t("label.period")}</Text>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange as any}
                onChange={(v) => setDateRange(v as any)}
                allowClear
              />
            </Space>
          </Col>

          <Col xs={24} sm={24} md={24} lg={6}>
            <Space direction="vertical" style={{ width: "100%", marginTop: 22 }} size="small">
              <Button type="primary" onClick={onSearch} loading={loading} block>
                {t("button.search")}
              </Button>
              <Button onClick={onReset} block>
                {t("button.reset_filters")}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* KPIs */}
      {statistics && (
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Total Orders" value={statistics.totalOrders} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Total Revenue" value={statistics.totalRevenue} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Average Order Value" value={statistics.averageOrderValue} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Cancellation Rate" value={statistics.cancellationRate} suffix="%" precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Cancelled Orders" value={statistics.cancelledOrders} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Subtotal" value={statistics.totalSubtotal} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Discounts" value={statistics.totalDiscounts} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Delivery Fees" value={statistics.totalDeliveryFees} precision={2} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card>
              <Statistic title="Tips" value={statistics.totalTips} precision={2} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Tables */}
      {statistics && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={12} lg={12}>
            <Card title="Daily Trend">
              <Table
                rowKey="date"
                size="small"
                dataSource={statistics.dailyTrend}
                pagination={false}
                scroll={{ x: true }}
                columns={[
                  { title: "Date", dataIndex: "date", key: "date" },
                  { title: "Orders", dataIndex: "orderCount", key: "orderCount" },
                  { title: "Revenue", dataIndex: "totalRevenue", key: "totalRevenue" },
                ]}
              />
            </Card>
          </Col>

          <Col xs={24} md={12} lg={12}>
            <Card title="Status Breakdown">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} style={{ display: "flex", justifyContent: "center" }}>
                  <div ref={donutRef} style={{ position: "relative", width: "100%", maxWidth: "200px", margin: "0 auto" }}>
                    <svg width="100%" height="auto" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Status breakdown" style={{ maxWidth: "200px", height: "auto" }}>
                      {(() => {
                        const cx = 100;
                        const cy = 100;
                        const rOuter = 95;
                        const rInner = 60;
                        let angle = 0;
                        const arcs: JSX.Element[] = [];
                        (slices || []).forEach((sl, idx) => {
                          const sweep = (sl.percentage / 100) * 360;
                          const start = angle;
                          const end = angle + sweep;
                          angle = end;
                          if (sweep <= 0) return;
                          const d = arcPath(cx, cy, rOuter, rInner, start, end);
                          arcs.push(
                            <path
                              key={`${sl.status}-${idx}`}
                              d={d}
                              fill={sl.color}
                              onMouseMove={(e) => {
                                const rect = donutRef.current?.getBoundingClientRect();
                                const content = `${t(`status.${sl.status.toLowerCase()}`)}: ${sl.percentage}% (${sl.count})`;
                                if (rect) {
                                  setTooltip({
                                    visible: true,
                                    x: e.clientX - rect.left + 8,
                                    y: e.clientY - rect.top + 8,
                                    content,
                                  });
                                }
                              }}
                              onMouseLeave={() => setTooltip((prev) => ({ ...prev, visible: false }))}
                            >
                              <title>
                                {`${t(`status.${sl.status.toLowerCase()}`)}: ${sl.percentage}% (${sl.count})`}
                              </title>
                            </path>
                          );
                        });
                        return arcs;
                      })()}
                    </svg>
                    {tooltip.visible && (
                      <div
                        style={{
                          position: "absolute",
                          left: tooltip.x,
                          top: tooltip.y,
                          background: "rgba(0,0,0,0.75)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: 4,
                          fontSize: 12,
                          pointerEvents: "none",
                          whiteSpace: "nowrap",
                          transform: "translate(-50%, -120%)",
                        }}
                      >
                        {tooltip.content}
                      </div>
                    )}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        width: 200,
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>Total Orders</div>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>{statistics.totalOrders}</div>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {(slices || []).map((sl) => (
                      <Space key={sl.status} style={{ justifyContent: "space-between", width: "100%" }}>
                        <Space>
                          <span
                            style={{
                              display: "inline-block",
                              width: 12,
                              height: 12,
                              borderRadius: 2,
                              background: sl.color,
                            }}
                          />
                          <span>{t(`status.${sl.status.toLowerCase()}`)}</span>
                        </Space>
                        <Text type="secondary">{sl.percentage}% ({sl.count})</Text>
                      </Space>
                    ))}
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24} md={12} lg={12}>
            <Card title="Top Stores" style={{ marginTop: 16 }}>
              <Table
                rowKey="storeId"
                size="small"
                dataSource={statistics.topStores}
                pagination={false}
                scroll={{ x: true }}
                columns={[
                  { title: "Store", dataIndex: "storeName", key: "storeName" },
                  { title: "Orders", dataIndex: "orderCount", key: "orderCount" },
                  {
                    title: "Avg Order Value",
                    dataIndex: "averageOrderValue",
                    key: "averageOrderValue",
                  },
                  { title: "Revenue", dataIndex: "totalRevenue", key: "totalRevenue" },
                ]}
              />
            </Card>
          </Col>

          {/* Top Partners removed per requirement */}
        </Row>
      )}
    </RobinPage>
  );
}

export default Dashboard;
